import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useToast } from './ToastContext';
import {
  CollaborationState,
  CollaborationMode,
  CollaborationContextValue,
  CollaborationUser,
  CollaborationSession,
  UpdateData,
  UserCursor,
  Message,
  JoinedMessage,
  UserJoinedMessage,
  UserLeftMessage,
  UpdateMessage,
  CursorUpdateMessage,
  SelectionUpdateMessage,
  SyncResponseMessage,
  ErrorMessage,
  KickedMessage,
  ShapeData,
  ShapeLock,
  LockType,
  LockGrantedMessage,
  LockDeniedMessage,
  LockReleasedMessage,
  LockUpdateMessage,
  LOCK_DURATIONS,
  generateUserId,
  generateUserColor,
  generateSessionId,
} from '../types/Collaboration';

/**
 * Initial collaboration state
 */
const initialState: CollaborationState = {
  mode: 'local',
  connectionState: 'disconnected',
  session: null,
  currentUser: null,
  users: [],
  error: null,
};

/**
 * Action types for reducer
 */
type CollaborationAction =
  | { type: 'SET_MODE'; payload: CollaborationMode }
  | { type: 'SET_CONNECTION_STATE'; payload: CollaborationState['connectionState'] }
  | { type: 'SET_SESSION'; payload: CollaborationSession | null }
  | { type: 'SET_CURRENT_USER'; payload: CollaborationUser | null }
  | { type: 'SET_USERS'; payload: CollaborationUser[] }
  | { type: 'ADD_USER'; payload: CollaborationUser }
  | { type: 'REMOVE_USER'; payload: string }
  | { type: 'UPDATE_USER_CURSOR'; payload: { userId: string; cursor: UserCursor | null } }
  | { type: 'UPDATE_USER_SELECTION'; payload: { userId: string; selection: string[] } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

/**
 * Reducer for collaboration state
 */
function collaborationReducer(
  state: CollaborationState,
  action: CollaborationAction
): CollaborationState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'SET_CONNECTION_STATE':
      return { ...state, connectionState: action.payload };

    case 'SET_SESSION':
      return { ...state, session: action.payload };

    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };

    case 'SET_USERS':
      return { ...state, users: action.payload };

    case 'ADD_USER':
      // Avoid duplicates
      if (state.users.some((u) => u.id === action.payload.id)) {
        return state;
      }
      return { ...state, users: [...state.users, action.payload] };

    case 'REMOVE_USER':
      return {
        ...state,
        users: state.users.filter((u) => u.id !== action.payload),
      };

    case 'UPDATE_USER_CURSOR':
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === action.payload.userId
            ? {
                ...u,
                presence: {
                  ...u.presence,
                  cursor: action.payload.cursor,
                  viewport: u.presence?.viewport ?? null,
                  selection: u.presence?.selection ?? [],
                  lastUpdate: Date.now(),
                },
              }
            : u
        ),
      };

    case 'UPDATE_USER_SELECTION':
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === action.payload.userId
            ? {
                ...u,
                presence: {
                  ...u.presence,
                  cursor: u.presence?.cursor ?? null,
                  viewport: u.presence?.viewport ?? null,
                  selection: action.payload.selection,
                  lastUpdate: Date.now(),
                },
              }
            : u
        ),
      };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

/**
 * Collaboration context
 */
const CollaborationContext = createContext<CollaborationContextValue | null>(null);

/**
 * Provider props
 */
interface CollaborationProviderProps {
  children: ReactNode;
  serverUrl?: string;
  onShapesUpdate?: (shapes: ShapeData[]) => void;
}

/**
 * Collaboration provider component
 */
export function CollaborationProvider({
  children,
  serverUrl = import.meta.env.VITE_WS_SERVER_URL || 'ws://localhost:3001',
  onShapesUpdate,
}: CollaborationProviderProps) {
  const [state, dispatch] = useReducer(collaborationReducer, initialState);
  const toast = useToast();

  // Remote shapes received from other users
  const [remoteShapes, setRemoteShapes] = useState<ShapeData[] | null>(null);

  // Remote update action and data (for incremental updates)
  // Using updateId counter instead of timestamp to ensure each update is unique
  const updateIdRef = useRef(0);
  const [remoteUpdate, setRemoteUpdate] = useState<{
    action: string;
    shapes?: ShapeData[];
    shapeIds?: string[];
    updateId: number;
  } | null>(null);

  // Lock state
  const [lockedShapes, setLockedShapes] = useState<Map<string, ShapeLock>>(new Map());
  const lockTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Track if user is voluntarily leaving session (vs forced disconnect)
  const isLeavingSessionRef = useRef<boolean>(false);

  // WebSocket connection
  const { connectionState, connect, disconnect, send, lastMessage, error: wsError } = useWebSocket({
    url: serverUrl,
    autoConnect: false,
    reconnect: true,
    maxReconnectAttempts: 5,
  });

  // Refs for callback handlers
  const onShapesUpdateRef = useRef(onShapesUpdate);
  const pendingJoinRef = useRef<{ roomId: string; userName: string } | null>(null);

  // Update ref when prop changes
  useEffect(() => {
    onShapesUpdateRef.current = onShapesUpdate;
  }, [onShapesUpdate]);

  // Sync connection state
  useEffect(() => {
    dispatch({ type: 'SET_CONNECTION_STATE', payload: connectionState });

    // If connected and there's a pending join, execute it
    if (connectionState === 'connected' && pendingJoinRef.current) {
      const { roomId, userName } = pendingJoinRef.current;
      const userId = state.currentUser?.id || generateUserId();

      send({
        type: 'join',
        roomId,
        userId,
        userName,
        timestamp: Date.now(),
      });

      pendingJoinRef.current = null;
    }
  }, [connectionState, send, state.currentUser?.id]);

  // Handle WebSocket errors
  useEffect(() => {
    if (wsError) {
      dispatch({ type: 'SET_ERROR', payload: wsError });
    }
  }, [wsError]);

  // Handle incoming messages
  useEffect(() => {
    if (!lastMessage) return;

    switch (lastMessage.type) {
      case 'joined': {
        const msg = lastMessage as JoinedMessage;

        // Create session info
        const session: CollaborationSession = {
          id: msg.roomId,
          name: msg.roomId, // Use roomId as name for now
          ownerId: null,
          status: 'active',
          userCount: msg.users.length,
          maxUsers: 10,
          isPublic: true,
          createdAt: Date.now(),
        };

        dispatch({ type: 'SET_SESSION', payload: session });
        dispatch({ type: 'SET_MODE', payload: 'collaborative' });

        // Set users (convert server user type to client type)
        const users: CollaborationUser[] = msg.users.map((u: any) => ({
          id: u.id,
          name: u.name,
          color: u.color,
          isOnline: true,
          presence: u.presence,
        }));
        dispatch({ type: 'SET_USERS', payload: users });

        // Apply initial shapes from room state
        if (msg.state?.shapes) {
          console.log('[Collaboration] Joined session with', msg.state.shapes.length, 'shapes');
          setRemoteShapes(msg.state.shapes);
          if (onShapesUpdateRef.current) {
            onShapesUpdateRef.current(msg.state.shapes);
          }
        }
        break;
      }

      case 'user_joined': {
        const msg = lastMessage as UserJoinedMessage;
        const newUser: CollaborationUser = {
          id: msg.user.id,
          name: msg.user.name,
          color: msg.user.color,
          isOnline: true,
        };
        dispatch({ type: 'ADD_USER', payload: newUser });
        break;
      }

      case 'user_left': {
        const msg = lastMessage as UserLeftMessage;
        dispatch({ type: 'REMOVE_USER', payload: msg.userId });
        break;
      }

      case 'kicked': {
        const msg = lastMessage as KickedMessage;
        // I was kicked from the session
        toast.warning(`세션에서 내보내졌습니다. (by ${msg.kickedBy})`);
        disconnect();
        dispatch({ type: 'RESET' });
        break;
      }

      case 'update': {
        const msg = lastMessage as UpdateMessage;
        // Don't apply own updates
        if (msg.userId !== state.currentUser?.id && msg.data) {
          const action = msg.data.action || 'bulk';

          // Set remote update with action for proper incremental updates
          // Increment updateId to ensure each update is unique and processed
          updateIdRef.current += 1;
          setRemoteUpdate({
            action,
            shapes: msg.data.shapes,
            shapeIds: msg.data.shapeIds,
            updateId: updateIdRef.current,
          });

          // Also update remoteShapes for bulk updates (backward compatibility)
          // This handles both explicit 'bulk' action and missing action (legacy)
          if (action === 'bulk' && msg.data.shapes) {
            setRemoteShapes(msg.data.shapes);
            if (onShapesUpdateRef.current) {
              onShapesUpdateRef.current(msg.data.shapes);
            }
          }
        }
        break;
      }

      case 'cursor_update': {
        const msg = lastMessage as CursorUpdateMessage;
        // Don't update own cursor
        if (msg.userId !== state.currentUser?.id) {
          dispatch({
            type: 'UPDATE_USER_CURSOR',
            payload: { userId: msg.userId, cursor: msg.cursor },
          });
        }
        break;
      }

      case 'selection_update': {
        const msg = lastMessage as SelectionUpdateMessage;
        // Don't update own selection
        if (msg.userId !== state.currentUser?.id) {
          dispatch({
            type: 'UPDATE_USER_SELECTION',
            payload: { userId: msg.userId, selection: msg.selection },
          });
        }
        break;
      }

      case 'sync_response': {
        const msg = lastMessage as SyncResponseMessage;

        // Update users
        const users: CollaborationUser[] = msg.users.map((u: any) => ({
          id: u.id,
          name: u.name,
          color: u.color,
          isOnline: true,
          presence: u.presence,
        }));
        dispatch({ type: 'SET_USERS', payload: users });

        // Apply shapes from sync response
        if (msg.state?.shapes) {
          console.log('[Collaboration] Sync response with', msg.state.shapes.length, 'shapes');
          setRemoteShapes(msg.state.shapes);
          if (onShapesUpdateRef.current) {
            onShapesUpdateRef.current(msg.state.shapes);
          }
        }
        break;
      }

      case 'error': {
        const msg = lastMessage as ErrorMessage;
        dispatch({ type: 'SET_ERROR', payload: msg.error });
        break;
      }

      // Lock messages
      case 'lock_granted': {
        const msg = lastMessage as LockGrantedMessage;
        setLockedShapes((prev) => {
          const newMap = new Map(prev);
          newMap.set(msg.shapeId, msg.lock);
          return newMap;
        });

        // Set auto-release timeout
        const timeout = setTimeout(() => {
          setLockedShapes((prev) => {
            const newMap = new Map(prev);
            newMap.delete(msg.shapeId);
            return newMap;
          });
          lockTimeoutsRef.current.delete(msg.shapeId);
        }, msg.lock.expiresAt - Date.now());

        lockTimeoutsRef.current.set(msg.shapeId, timeout);
        break;
      }

      case 'lock_denied': {
        const msg = lastMessage as LockDeniedMessage;
        console.warn(`[Collaboration] Lock denied for shape ${msg.shapeId}: ${msg.reason}`);
        // Could dispatch an error or show toast here
        break;
      }

      case 'lock_released': {
        const msg = lastMessage as LockReleasedMessage;
        setLockedShapes((prev) => {
          const newMap = new Map(prev);
          newMap.delete(msg.shapeId);
          return newMap;
        });

        // Clear timeout
        const timeout = lockTimeoutsRef.current.get(msg.shapeId);
        if (timeout) {
          clearTimeout(timeout);
          lockTimeoutsRef.current.delete(msg.shapeId);
        }
        break;
      }

      case 'lock_update': {
        const msg = lastMessage as LockUpdateMessage;
        setLockedShapes((prev) => {
          const newMap = new Map(prev);
          if (msg.lock) {
            newMap.set(msg.shapeId, msg.lock);

            // Set auto-release timeout
            const existingTimeout = lockTimeoutsRef.current.get(msg.shapeId);
            if (existingTimeout) {
              clearTimeout(existingTimeout);
            }

            const timeout = setTimeout(() => {
              setLockedShapes((p) => {
                const m = new Map(p);
                m.delete(msg.shapeId);
                return m;
              });
              lockTimeoutsRef.current.delete(msg.shapeId);
            }, msg.lock.expiresAt - Date.now());

            lockTimeoutsRef.current.set(msg.shapeId, timeout);
          } else {
            newMap.delete(msg.shapeId);
            const timeout = lockTimeoutsRef.current.get(msg.shapeId);
            if (timeout) {
              clearTimeout(timeout);
              lockTimeoutsRef.current.delete(msg.shapeId);
            }
          }
          return newMap;
        });
        break;
      }
    }
  }, [lastMessage, state.currentUser?.id]);

  /**
   * Create a new collaboration session
   */
  const createSession = useCallback(
    (name: string) => {
      const sessionId = generateSessionId();
      const userId = generateUserId();
      const userColor = generateUserColor();

      // Create current user
      const currentUser: CollaborationUser = {
        id: userId,
        name: name,
        color: userColor,
        isOnline: true,
      };
      dispatch({ type: 'SET_CURRENT_USER', payload: currentUser });

      // Build room-specific WebSocket URL for Cloudflare Workers routing
      const roomWsUrl = `${serverUrl.replace(/\/$/, '')}/ws/${sessionId}`;

      // Store pending join info and connect with room-specific URL
      pendingJoinRef.current = { roomId: sessionId, userName: name };
      connect(roomWsUrl);
    },
    [connect, serverUrl]
  );

  /**
   * Join an existing session
   */
  const joinSession = useCallback(
    (sessionId: string, userName: string) => {
      const userId = state.currentUser?.id || generateUserId();
      const userColor = state.currentUser?.color || generateUserColor();

      // Create/update current user
      const currentUser: CollaborationUser = {
        id: userId,
        name: userName,
        color: userColor,
        isOnline: true,
      };
      dispatch({ type: 'SET_CURRENT_USER', payload: currentUser });

      // Build room-specific WebSocket URL for Cloudflare Workers routing
      const roomWsUrl = `${serverUrl.replace(/\/$/, '')}/ws/${sessionId}`;

      // If already connected, send join directly
      if (connectionState === 'connected') {
        send({
          type: 'join',
          roomId: sessionId,
          userId,
          userName,
          timestamp: Date.now(),
        });
      } else {
        // Store pending join info and connect with room-specific URL
        pendingJoinRef.current = { roomId: sessionId, userName };
        connect(roomWsUrl);
      }
    },
    [state.currentUser, connectionState, connect, send, serverUrl]
  );

  /**
   * Leave the current session
   */
  const leaveSession = useCallback(() => {
    // Mark as voluntarily leaving
    isLeavingSessionRef.current = true;

    if (state.session && state.currentUser) {
      send({
        type: 'leave',
        roomId: state.session.id,
        userId: state.currentUser.id,
        timestamp: Date.now(),
      });
    }

    disconnect();
    dispatch({ type: 'RESET' });

    // Reset flag after a delay to allow components to check it
    // 세션 종료 감지가 완료될 때까지 충분한 시간 제공
    setTimeout(() => {
      isLeavingSessionRef.current = false;
    }, 1000);
  }, [state.session, state.currentUser, send, disconnect]);

  /**
   * Kick a user from the session
   */
  const kickUser = useCallback((targetUserId: string) => {
    if (state.session && state.currentUser) {
      send({
        type: 'kick',
        roomId: state.session.id,
        userId: state.currentUser.id,
        targetUserId,
        timestamp: Date.now(),
      });
    }
  }, [state.session, state.currentUser, send]);

  /**
   * Set collaboration mode
   */
  const setMode = useCallback((mode: CollaborationMode) => {
    dispatch({ type: 'SET_MODE', payload: mode });
  }, []);

  /**
   * Send canvas update
   */
  const sendUpdate = useCallback(
    (data: UpdateData) => {
      if (state.session && state.currentUser && state.mode === 'collaborative') {
        send({
          type: 'update',
          roomId: state.session.id,
          userId: state.currentUser.id,
          data,
          timestamp: Date.now(),
        });
      }
    },
    [state.session, state.currentUser, state.mode, send]
  );

  /**
   * Send cursor position
   */
  const sendCursor = useCallback(
    (cursor: UserCursor | null) => {
      if (state.session && state.currentUser && state.mode === 'collaborative') {
        send({
          type: 'cursor',
          roomId: state.session.id,
          userId: state.currentUser.id,
          cursor,
          timestamp: Date.now(),
        });
      }
    },
    [state.session, state.currentUser, state.mode, send]
  );

  /**
   * Send selection (selected shape IDs)
   */
  const sendSelection = useCallback(
    (selection: string[]) => {
      if (state.session && state.currentUser && state.mode === 'collaborative') {
        send({
          type: 'selection',
          roomId: state.session.id,
          userId: state.currentUser.id,
          selection,
          timestamp: Date.now(),
        });
      }
    },
    [state.session, state.currentUser, state.mode, send]
  );

  /**
   * Request a lock on a shape
   */
  const requestLock = useCallback(
    (shapeId: string, lockType: LockType) => {
      if (state.session && state.currentUser && state.mode === 'collaborative') {
        send({
          type: 'lock_request',
          roomId: state.session.id,
          userId: state.currentUser.id,
          shapeId,
          lockType,
          timestamp: Date.now(),
        });
      }
    },
    [state.session, state.currentUser, state.mode, send]
  );

  /**
   * Release a lock on a shape
   */
  const releaseLock = useCallback(
    (shapeId: string) => {
      if (state.session && state.currentUser && state.mode === 'collaborative') {
        send({
          type: 'lock_release',
          roomId: state.session.id,
          userId: state.currentUser.id,
          shapeId,
          timestamp: Date.now(),
        });

        // Also update local state immediately
        setLockedShapes((prev) => {
          const newMap = new Map(prev);
          const lock = newMap.get(shapeId);
          if (lock && lock.userId === state.currentUser?.id) {
            newMap.delete(shapeId);
          }
          return newMap;
        });

        // Clear timeout
        const timeout = lockTimeoutsRef.current.get(shapeId);
        if (timeout) {
          clearTimeout(timeout);
          lockTimeoutsRef.current.delete(shapeId);
        }
      }
    },
    [state.session, state.currentUser, state.mode, send]
  );

  /**
   * Check if a shape is locked (by anyone)
   */
  const isShapeLocked = useCallback(
    (shapeId: string): boolean => {
      const lock = lockedShapes.get(shapeId);
      if (!lock) return false;

      // Check if lock is expired
      if (Date.now() > lock.expiresAt) {
        return false;
      }

      return true;
    },
    [lockedShapes]
  );

  /**
   * Check if a shape is locked by the current user
   */
  const isShapeLockedByMe = useCallback(
    (shapeId: string): boolean => {
      const lock = lockedShapes.get(shapeId);
      if (!lock) return false;

      // Check if lock is expired
      if (Date.now() > lock.expiresAt) {
        return false;
      }

      return lock.userId === state.currentUser?.id;
    },
    [lockedShapes, state.currentUser?.id]
  );

  /**
   * Get lock information for a shape
   */
  const getShapeLock = useCallback(
    (shapeId: string): ShapeLock | null => {
      const lock = lockedShapes.get(shapeId);
      if (!lock) return null;

      // Check if lock is expired
      if (Date.now() > lock.expiresAt) {
        return null;
      }

      return lock;
    },
    [lockedShapes]
  );

  // Cleanup lock timeouts on unmount
  useEffect(() => {
    return () => {
      lockTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      lockTimeoutsRef.current.clear();
    };
  }, []);

  const contextValue: CollaborationContextValue = {
    state,
    createSession,
    joinSession,
    leaveSession,
    kickUser,
    setMode,
    sendUpdate,
    sendCursor,
    sendSelection,
    lockedShapes,
    requestLock,
    releaseLock,
    isShapeLocked,
    isShapeLockedByMe,
    getShapeLock,
    onShapesUpdate,
    remoteShapes,
    remoteUpdate,
    isLeavingSession: () => isLeavingSessionRef.current,
  };

  return (
    <CollaborationContext.Provider value={contextValue}>
      {children}
    </CollaborationContext.Provider>
  );
}

/**
 * Hook to use collaboration context
 */
export function useCollaboration(): CollaborationContextValue {
  const context = useContext(CollaborationContext);
  if (!context) {
    throw new Error('useCollaboration must be used within a CollaborationProvider');
  }
  return context;
}

export default CollaborationContext;
